import React from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Button, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useFormik } from 'formik'
import * as yup from 'yup'

import UserAPI from '../../api/orbitV1/UserAPI'
import PageLayout from '../../common/Layout/Page/PageLayout'
import BreadCrumbPaths from '../../common/ui/breadCrumbs'
import { ColorBoxContent } from '../../common/ui/colorBox'
import OrbitSession from '../../hooks/useCookies'

// hold for POST confirmation
// import { useQuery } from '../hooks'
import { AccountForm } from './components'

export default function AddAccount() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // hold for POST confirmation
  const orgId = OrbitSession.get('orgId')
  const userRole = OrbitSession.get('role')

  /* Formik variables */
  const validationSchema = yup.object({
    contactName: yup.string().required('Contact Name is required'),
    contactNumber: yup
      .string()
      .matches(/^([689])\d{7}$/, 'Invalid Contact Number')
      .required('Contact Number is required'),
    email: yup
      .string()
      .email('Enter a valid email')
      .required('Email is required'),
    role: yup.string()
  })

  const addAccFormik = useFormik({
    initialValues: {
      contactName: '',
      contactNumber: '',
      email: '',
      role: userRole
    },
    validationSchema,
    onSubmit: (values) => {
      toast.error(JSON.stringify(values))
    }
  })

  const baseRoute = window.location.pathname.includes('/app/')
    ? '/app/manage-accounts'
    : '/hq/manage-accounts'

  // Pending completion of components
  const handleFormSubmit = async (e) => {
    e.preventDefault()

    const { contactName, contactNumber, email, role } = addAccFormik.values

    // compose submission object, awaiting API
    const requestBody = {
      name: contactName,
      contactNumber,
      email,
      role,
      orgId
    }

    try {
      const createUserResponse = await UserAPI.createUser(requestBody)
      if (createUserResponse) {
        toast.success('User Account successfully created!', {
          autoClose: 3000,
          hideProgressBar: false
        })
        setTimeout(() => {
          queryClient.invalidateQueries({
            predicate: (query) =>
              query.queryKey[0] === 'getOrgDetails' &&
              query.queryKey[1] === 'accountManagement'
          })
          navigate(baseRoute)
        }, 3000)
      }
    } catch (err) {
      toast.error('Error creating user')
    }
  }

  const handleCancel = () => {
    navigate(-1)
  }

  return (
    <PageLayout header='Add New User'>
      <BreadCrumbPaths
        paths={[
          {
            route: baseRoute,
            name: 'User Accounts'
          },
          {
            route: '',
            name: 'Add New User'
          }
        ]}
      />
      <ColorBoxContent style={{ marginBottom: 12 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8'
          }}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%'
          }}
        >
          <Typography variant='h4'>New User Information</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <AccountForm formik={addAccFormik} updateSelected />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-around',
                gap: 2
              }}
            >
              <Button
                variant='secondary'
                onClick={handleCancel}
                data-cy='newUserCancelBtn'
              >
                Cancel
              </Button>
              <Button
                variant='filled'
                onClick={handleFormSubmit}
                data-cy='newUserConfirmBtn'
              >
                Confirm
              </Button>
            </Box>
          </Box>
        </Box>
      </ColorBoxContent>
      {/* Commented for project planning
       * New user information box to assign new admin/owner
       * submission calls backend to add such usr (need to auth with google?)
       * and validate before assigning the appropriate permissions
       */}
    </PageLayout>
  )
}
